import { Calendar, File06, Shield01, Stars02, TrendUp01, Users01 } from "@untitledui/icons";
import { type FC, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { createPortal } from "react-dom";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import {
  DateRangePicker,
  type ReportRequest,
  type TemplateMeta,
  useVisibleReportTemplates,
} from "@/pages/ask/components/ReportPicker";

const TEMPLATE_ICONS: Record<TemplateMeta["icon"], FC<{ className?: string }>> = {
  shield: Shield01,
  sparkle: Stars02,
  users: Users01,
  trending: TrendUp01,
  calendar: Calendar,
};

type HeroReportPickerProps = {
  brandName: string;
  onGenerate: (report: ReportRequest) => void;
};

export function HeroReportPicker({ brandName, onGenerate }: HeroReportPickerProps) {
  const templates = useVisibleReportTemplates();
  const [pendingDateRange, setPendingDateRange] = useState<TemplateMeta | null>(null);

  const pick = (template: TemplateMeta) => {
    if (template.requiresDateRange) setPendingDateRange(template);
    else onGenerate({ template });
  };

  return (
    <>
      <Dropdown.Root>
        <AriaButton className="flex h-full shrink-0 cursor-pointer items-center gap-1.5 px-4 text-sm text-white/70 outline-focus-ring transition-colors duration-150 hover:text-white focus-visible:outline-2 focus-visible:-outline-offset-2 pressed:text-white">
          <File06 width={18} height={18} />
          Reports
        </AriaButton>
        <Dropdown.Popover placement="bottom end" className="w-80">
          <Dropdown.Menu
            aria-label="Smart reports"
            selectionMode="none"
            onAction={(key) => {
              const template = templates.find((t) => t.id === key);
              if (template) pick(template);
            }}
          >
            <Dropdown.Section>
              <Dropdown.SectionHeader className="px-4 pt-2 pb-1 text-xs font-semibold tracking-wide text-quaternary uppercase">
                Smart reports
              </Dropdown.SectionHeader>
              {templates.map((template) => (
                <Dropdown.Item
                  key={template.id}
                  id={template.id}
                  label={template.title}
                  textValue={template.title}
                  icon={TEMPLATE_ICONS[template.icon]}
                />
              ))}
            </Dropdown.Section>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>

      {pendingDateRange &&
        createPortal(
          <DateRangePicker
            templateId={pendingDateRange.id}
            templateTitle={pendingDateRange.title}
            brandName={brandName}
            onConfirm={(startDate, endDate) => {
              setPendingDateRange(null);
              onGenerate({
                template: pendingDateRange,
                additionalInputs: { start_date: startDate, end_date: endDate },
              });
            }}
            onCancel={() => setPendingDateRange(null)}
          />,
          document.body,
        )}
    </>
  );
}
